import { IObservable, Subject } from '~/rage/utils';
import {
	IGasStationInfo,
	VehicleEngineType,
	VehicleFuelType,
	getVehicleFuelTypeName,
} from '~/shared';

import { RpcService } from '../RpcService';
import { IVehicleService } from '../VehicleService';
import { IGasStationService } from './gasStationServiceTypes';

export class GasStationService implements IGasStationService {
	private gasStationInfo = new Subject<IGasStationInfo>({
		engineType: VehicleEngineType.Petrol,
		fuelTypes: {},
		id: 0,
	});

	constructor(
		private readonly rpc: RpcService,
		private readonly vehicles: IVehicleService,
	) {
		rpc.register('gasStation:setInfo', (info: IGasStationInfo) => {
			this.gasStationInfo.next(info);
		});
	}

	public getActiveGasStationInfo(): IObservable<IGasStationInfo> {
		return this.gasStationInfo;
	}

	public getFuelTypeName(fuelType: VehicleFuelType): string {
		return getVehicleFuelTypeName(fuelType);
	}

	public getTotalPrice(fuelType: VehicleFuelType, amount?: number): number {
		const fuelPrice = this.gasStationInfo.getValue().fuelTypes[fuelType];
		if (fuelPrice === undefined) {
			return 0;
		}

		const currentFuelAmount = this.vehicles
			.getLocalVehicleFuelTankCapacity()
			.getValue();
		const fuelTankCapacity = this.vehicles
			.getLocalVehicleFuelTankCapacity()
			.getValue();

		let fuelAmount = 0;
		if (amount === undefined) {
			fuelAmount = fuelTankCapacity - currentFuelAmount;
		} else {
			if (amount <= 0) {
				return 0;
			}

			fuelAmount = amount;
			if (currentFuelAmount + fuelAmount > fuelTankCapacity) {
				fuelAmount = fuelTankCapacity - currentFuelAmount;
			}
		}

		return fuelPrice * fuelAmount;
	}

	public async buyFuel(
		fuelType: VehicleFuelType,
		amount?: number,
	): Promise<void> {
		if (amount && Math.floor(amount) < 1) {
			return;
		}

		await this.rpc.invokeServer('gasStation:buyFuel', {
			gasStationId: this.getActiveGasStationInfo().getValue().id,
			fuelType,
			amount,
		});

		this.closePanel();
	}

	public async drainFuel(amount?: number): Promise<void> {
		await this.rpc.invokeServer('gasStation:drainFuel', { amount });
	}

	public async closePanel(): Promise<void> {
		return await this.rpc.invokeClient('gasStation:close');
	}
}
